/**
 * Payments App Component
 *
 * Main component for the Payments admin page
 */

import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Spinner } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import Header from './components/Header';
import PaymentStatusBadge from './components/PaymentStatusBadge';
import PaymentDetailsModal from './components/PaymentDetailsModal';
import Pagination from './components/Pagination';
import { Icon, external } from '@wordpress/icons';
import {
    formatCurrency,
    formatDateOnly,
    getGatewayLabel,
    shortenId,
    copyToClipboard,
} from './utils/paymentHelpers';

const PaymentsApp = () => {
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [orderby, setOrderby] = useState('created_at');
    const [order, setOrder] = useState('desc');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const fetchPayments = async () => {
            setIsLoading(true);
            try {
                const params = new URLSearchParams({
                    page: currentPage,
                    per_page: itemsPerPage,
                    orderby,
                    order,
                });

                if (searchDebounced) {
                    params.append('search', searchDebounced);
                }

                const response = await apiFetch({
                    path: `/nemtly-booking/v1/payments?${params.toString()}`,
                    parse: false,
                });

                const data = await response.json();
                const total = parseInt(response.headers.get('X-WP-Total'), 10);
                const pages = parseInt(response.headers.get('X-WP-TotalPages'), 10);

                setPayments(data);
                setTotalItems(total);
                setTotalPages(pages);
            } catch (error) {
                // Silently failed; user will see empty list
            } finally {
                setIsLoading(false);
            }
        };

        fetchPayments();
    }, [currentPage, itemsPerPage, searchDebounced, orderby, order, refreshTrigger]);

    const handleViewDetails = (payment) => {
        setSelectedPayment(payment);
        setIsModalOpen(true);
    };

    const handleSort = (field) => {
        if (orderby === field) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderby(field);
            setOrder('desc');
        }
        setCurrentPage(1);
    };

    const SortHeader = ({ field, label, hideBelowLg = false }) => (
        <th
            scope="col"
            className={`nb-py-3.5 nb-px-4 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900 nb-cursor-pointer hover:nb-bg-gray-100 nb-select-none ${hideBelowLg ? 'nb-hidden lg:nb-table-cell' : ''}`}
            onClick={() => handleSort(field)}
        >
            <span className="nb-inline-flex nb-items-center nb-gap-1">
                {label}
                {orderby === field && (
                    <span className="nb-text-gray-500" aria-hidden="true">
                        {order === 'asc' ? '↑' : '↓'}
                    </span>
                )}
            </span>
        </th>
    );

    return (
        <div className="nb-payments-app">
            <Header
                version={window.nemtlyPayments?.version || ''}
                pageName={__('Payments', 'nemtly-booking')}
            />
            <div className="nb-payments-list-content nb-p-6">
                <div className="nb-bg-white nb-rounded-lg nb-shadow">
                <div className="nb-p-4 nb-border-b nb-border-gray-200">
                    <input
                        type="search"
                        placeholder={__('Search payments...', 'nemtly-booking')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="nb-w-full nb-max-w-md nb-px-3 nb-py-2 nb-border nb-border-gray-300 nb-rounded-md nb-text-sm focus:nb-ring-2 focus:nb-ring-indigo-500 focus:nb-border-indigo-500"
                        aria-label={__('Search payments', 'nemtly-booking')}
                    />
                </div>

                <div className="nb-relative nb-overflow-x-auto">
                    {isLoading && (
                        <div className="nb-absolute nb-inset-0 nb-bg-white nb-bg-opacity-75 nb-flex nb-items-center nb-justify-center nb-z-10">
                            <Spinner />
                        </div>
                    )}

                    <table className="nb-min-w-full nb-divide-y nb-divide-gray-200">
                        <thead className="nb-bg-gray-50">
                            <tr>
                                <th
                                    scope="col"
                                    className="nb-py-3.5 nb-pl-4 nb-pr-3 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
                                >
                                    {__('ID', 'nemtly-booking')}
                                </th>
                                <th
                                    scope="col"
                                    className="nb-py-3.5 nb-px-4 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
                                >
                                    {__('Booking', 'nemtly-booking')}
                                </th>
                                <th
                                    scope="col"
                                    className="nb-py-3.5 nb-px-4 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
                                >
                                    {__('Customer', 'nemtly-booking')}
                                </th>
                                <SortHeader field="amount" label={__('Amount', 'nemtly-booking')} />
                                <SortHeader field="status" label={__('Status', 'nemtly-booking')} />
                                <SortHeader field="gateway" label={__('Gateway', 'nemtly-booking')} hideBelowLg />
                                <th
                                    scope="col"
                                    className="nb-hidden lg:nb-table-cell nb-py-3.5 nb-px-4 nb-text-left nb-text-sm nb-font-semibold nb-text-gray-900"
                                >
                                    {__('Transaction ID', 'nemtly-booking')}
                                </th>
                                <SortHeader field="created_at" label={__('Date', 'nemtly-booking')} />
                                <th scope="col" className="nb-relative nb-py-3.5 nb-pl-3 nb-pr-4 sm:nb-pr-6">
                                    <span className="nb-sr-only">{__('Actions', 'nemtly-booking')}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="nb-divide-y nb-divide-gray-200 nb-bg-white">
                            {payments.length === 0 && !isLoading ? (
                                <tr>
                                    <td
                                        colSpan="9"
                                        className="nb-py-12 nb-text-center nb-text-gray-500 nb-text-sm"
                                    >
                                        {__('No payments found.', 'nemtly-booking')}
                                    </td>
                                </tr>
                            ) : (
                                payments.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:nb-bg-gray-50 nb-cursor-pointer"
                                        onClick={() => handleViewDetails(item)}
                                    >
                                        <td className="nb-whitespace-nowrap nb-py-4 nb-pl-4 nb-pr-3 nb-text-sm nb-font-mono nb-text-gray-600">
                                            #{item.id}
                                        </td>
                                        <td className="nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm nb-text-gray-600">
                                            {item.booking ? (
                                                <div className="nb-space-y-1">
                                                    <div className="nb-font-mono nb-text-xs nb-text-gray-500">
                                                        {shortenId(item.booking.uuid, 6)}
                                                    </div>
                                                    {item.booking.event && (
                                                        <div className="nb-text-gray-900">
                                                            {item.booking.event.title}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td className="nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm nb-text-gray-600">
                                            {item.booking ? (
                                                <div className="nb-space-y-1">
                                                    <div className="nb-text-gray-900">
                                                        {item.booking.attendee_name}
                                                    </div>
                                                    <div className="nb-text-xs nb-text-gray-500">
                                                        {item.booking.attendee_email}
                                                    </div>
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td className="nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm">
                                            <span className="nb-font-semibold nb-text-gray-900">
                                                {formatCurrency(item.amount, item.currency)}
                                            </span>
                                        </td>
                                        <td className="nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm">
                                            <PaymentStatusBadge status={item.status} />
                                        </td>
                                        <td className="nb-hidden lg:nb-table-cell nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm">
                                            <span className="nb-inline-flex nb-items-center nb-rounded-full nb-bg-gray-100 nb-px-2.5 nb-py-0.5 nb-text-xs nb-font-medium nb-text-gray-800">
                                                {getGatewayLabel(item.gateway)}
                                            </span>
                                        </td>
                                        <td className="nb-hidden lg:nb-table-cell nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm">
                                            <div
                                                className="nb-flex nb-items-center nb-gap-2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <code className="nb-text-xs nb-text-gray-600">
                                                    {shortenId(item.transaction_id, 4)}
                                                </code>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        await copyToClipboard(item.transaction_id);
                                                    }}
                                                    className="nb-text-blue-600 hover:nb-text-blue-800 nb-text-xs"
                                                    title={__('Copy Transaction ID', 'nemtly-booking')}
                                                >
                                                    <svg
                                                        className="nb-h-4 nb-w-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                        <td className="nb-whitespace-nowrap nb-px-4 nb-py-4 nb-text-sm nb-text-gray-600">
                                            {formatDateOnly(item.created_at)}
                                        </td>
                                        <td className="nb-relative nb-whitespace-nowrap nb-py-4 nb-pl-3 nb-pr-4 nb-text-right nb-text-sm nb-font-medium sm:nb-pr-6">
                                            <div
                                                className="nb-flex nb-justify-end"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {item.booking && (
                                                    <a
                                                        href={`admin.php?page=nemtly-booking-bookings#/bookings/${item.booking.id}`}
                                                        className="nb-inline-flex nb-items-center nb-p-1 nb-text-gray-500 hover:nb-text-indigo-600 nb-rounded focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-indigo-500 focus:nb-ring-offset-1"
                                                        title={__('View booking', 'nemtly-booking')}
                                                    >
                                                        <Icon icon={external} size={20} />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="nb-p-4 nb-border-t nb-border-gray-200">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(perPage) => {
                            setItemsPerPage(perPage);
                            setCurrentPage(1);
                        }}
                        disabled={isLoading}
                        showInfo
                        showItemsPerPage
                    />
                </div>
            </div>
            </div>

            <PaymentDetailsModal
                payment={selectedPayment}
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedPayment(null);
                }}
                onMarkPaid={() => {
                    setRefreshTrigger((t) => t + 1);
                    setIsModalOpen(false);
                    setSelectedPayment(null);
                }}
            />
        </div>
    );
};

export default PaymentsApp;
